/**
 * AppContext — league data for the currently active league.
 *
 * Depends on AuthContext for token management.
 * The active service is always AwsRestService, scoped to the current leagueId.
 * switchLeague() swaps to a different league without a page reload.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { League, Player, GameRecord, Session, IDataService } from '../../shared/types';
import { AwsRestService } from '../services/awsRestService';
import { useAuth } from './AuthContext';

const API_URL = import.meta.env.VITE_API_URL as string;

// ── Context shape ─────────────────────────────────────────────��────────────

interface AppContextValue {
  league:  League | null;
  players: Player[];
  games:   GameRecord[];
  session: Session | null;
  sessionId: string;

  leagueId: string | null;
  service:  IDataService;
  switchLeague: (id: string) => void;
}

// No-op service used when no league is selected
class NullService implements IDataService {
  onLeague(cb: (v: League | null) => void)    { cb(null);   return () => {}; }
  onPlayers(cb: (v: Player[]) => void)        { cb([]);     return () => {}; }
  onGames(cb: (v: GameRecord[]) => void)      { cb([]);     return () => {}; }
  onSession(cb: (v: Session | null) => void)  { cb(null);   return () => {}; }
  async setLeague()   {}
  async addPlayer()   {}
  async removePlayer(){}
  async addGame()     {}
  async updateGame()  {}
  async setSession()  {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  destroy?(): void    {}
}

const NULL_SERVICE = new NullService();

const AppContext = createContext<AppContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────

export function AppProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();

  const [league,  setLeague]  = useState<League | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [games,   setGames]   = useState<GameRecord[]>([]);
  const [session, setSession] = useState<Session | null>(null);

  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('rp_session_id');
    if (existing) return existing;
    const fresh = Math.random().toString(36).slice(2);
    localStorage.setItem('rp_session_id', fresh);
    return fresh;
  });

  const [leagueId, setLeagueId]   = useState<string | null>(null);
  const [service,  setService]    = useState<IDataService>(NULL_SERVICE);
  const serviceRef = useRef<IDataService>(NULL_SERVICE);

  // Subscribe to the active service whenever it changes
  useEffect(() => {
    const unsubs = [
      service.onLeague(setLeague),
      service.onPlayers(setPlayers),
      service.onGames(setGames),
      service.onSession(setSession),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [service]);

  const switchLeague = useCallback((id: string) => {
    if (id === leagueId) return;

    // Destroy the previous service
    const prev = serviceRef.current;
    if (prev && typeof (prev as AwsRestService).destroy === 'function') {
      (prev as AwsRestService).destroy();
    }

    const svc = new AwsRestService({ apiUrl: API_URL, leagueId: id, getToken });
    serviceRef.current = svc;
    setLeagueId(id);
    setService(svc);
  }, [leagueId, getToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const svc = serviceRef.current;
      if (svc && typeof (svc as AwsRestService).destroy === 'function') {
        (svc as AwsRestService).destroy();
      }
    };
  }, []);

  return (
    <AppContext.Provider value={{
      league, players, games, session, sessionId,
      leagueId, service, switchLeague,
    }}>
      {children}
    </AppContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
